/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    Breadcrumbs,
    Inventory,
    PageHeader,
    ViewHeader,
    Counter,
    TableView,
    Row,
    Cell,
    FolderRail,
    FolderTree,
    Pagination,
    ItemActionButton,
    Search,
    FilterGroup,
    FilterRail
} from '@quarry/inventory';
import Datepicker from '@react/react-spectrum/Datepicker';
import FieldLabel from '@react/react-spectrum/FieldLabel';
import FolderOpenOutline from '@spectrum-icons/workflow/FolderOpenOutline';
import { defineMessages, useIntl } from 'react-intl';
import { useSelectionContext } from '../common/SelectionContext';
import ThumbnailCell from '../common/ThumbnailCell';
import { useOverlayFix } from '../common/useOverlayFix';
import { useProductPickerContext } from './ProductPickerContext';
import { trackEvent } from '../../utils/tracking';
import { event_productpicker_loaded, event_productpicker_stagedcatalog } from '../../utils/trackingEvents';
import { StatusLight } from '@adobe/react-spectrum';
import { useGraphqlSchemaContext } from '../GraphqlSchemaContext/GraphqlSchemaContext';

// i18n
const messages = defineMessages({
    rootCategory: {
        id: 'navigation.rootCategory',
        defaultMessage: 'Root Category'
    },
    columnName: {
        id: 'column.name',
        defaultMessage: 'Name'
    },
    columnType: {
        id: 'column.type',
        defaultMessage: 'Type'
    },
    columnPrice: {
        id: 'column.price',
        defaultMessage: 'Price'
    },
    columnSku: {
        id: 'column.sku',
        defaultMessage: 'SKU'
    },
    columnSlug: {
        id: 'column.slug',
        defaultMessage: 'Slug'
    },
    statusStagedData: {
        id: 'status.stagedData',
        defaultMessage: 'Staged Data'
    },
    viewVariants: {
        id: 'navigation.viewVariants',
        defaultMessage: 'View variants'
    },
    filterGroupStagedCatalog: {
        id: 'filterGroup.stagedCatalog',
        defaultMessage: 'Staged Catalog'
    },
    filterPreviewDate: {
        id: 'filter.previewDate',
        defaultMessage: 'Preview Date'
    }
});

const ProductPicker = props => {
    const { multiple, selectionId, enablePreviewDateFilter } = props;

    const ref = useRef();
    useOverlayFix(ref);

    const [, , [config]] = useGraphqlSchemaContext();
    const { formatMessage, formatNumber } = useIntl();
    const [{ pickerSelection }, { setPickerSelection }] = useSelectionContext();
    const [
        { items, pagination, path, loadingState, filters, folderTree, filterState },
        { onFolderExpand, onFilterChange }
    ] = useProductPickerContext();
    const displayName = config.name ? config.name : formatMessage(messages.rootCategory);

    const columns = [
        {
            key: 'label',
            label: formatMessage(messages.columnName)
        },
        {
            key: 'viewVariants',
            hideHeader: true,
            width: 40
        },
        {
            key: 'type',
            label: formatMessage(messages.columnType),
            width: 200
        },
        {
            key: 'price',
            label: formatMessage(messages.columnPrice),
            width: 100
        },
        {
            key: 'sku',
            label: formatMessage(messages.columnSku),
            width: 100
        },
        {
            key: 'slug',
            label: formatMessage(messages.columnSlug)
        }
    ];

    useEffect(() => {
        trackEvent(event_productpicker_loaded);
    }, []);

    const navigateVariants = selectionId === 'combinedSku' || selectionId === 'combinedSlug';
    const showPreviewData = filters.previewDate || config.previewDate;
    const filterRail = [];

    if (enablePreviewDateFilter && !config.previewDate) {
        // add the Preview Date filter only if enabled and no explit previewDate was configured
        filterRail.push(({ filterState, onFilterChange }) => (
            <FilterGroup key="stagedCatalog" title={formatMessage(messages.filterGroupStagedCatalog)}>
                <FieldLabel key="previewDate" label={formatMessage(messages.filterPreviewDate)}>
                    <Datepicker
                        data-test-id="filter-preview-date"
                        type="datetime"
                        min="today"
                        aria-label={formatMessage(messages.filterPreviewDate)}
                        value={filterState.previewDate || filters.previewDate}
                        onChange={date => {
                            trackEvent(event_productpicker_stagedcatalog);
                            onFilterChange('previewDate', date);
                        }}
                    />
                </FieldLabel>
            </FilterGroup>
        ));
    }

    return (
        <div data-test-id="product-picker">
            <span ref={ref} />
            <Inventory
                height="calc(100vh - 234px)"
                selectionMode={multiple ? 'multiple' : 'single'}
                items={items}
                pagination={pagination}
                loadingState={loadingState}
                onFilterChange={onFilterChange}
                selectedKeys={pickerSelection}
                onSelectionChange={setPickerSelection}
                defaultSelectedFilters={filterState}
            >
                <PageHeader>
                    <Breadcrumbs path={path} />
                    {showPreviewData && (
                        <StatusLight variant="notice">{formatMessage(messages.statusStagedData)}</StatusLight>
                    )}
                </PageHeader>
                <FolderRail defaultOpen>
                    <FolderTree
                        isLoading={folderTree.loadingState === 'loading'}
                        folders={folderTree.items}
                        rootFolder={displayName}
                        onFolderExpand={onFolderExpand}
                        path={path}
                    />
                </FolderRail>
                {filterRail.length > 0 && <FilterRail>{args => filterRail.map(filter => filter(args))}</FilterRail>}
                <ViewHeader>
                    {!showPreviewData && <Search wait={200} data-test-id="picker-search" />}
                    <Counter />
                </ViewHeader>
                <TableView data-test-id="product-table" columns={columns}>
                    {item => (
                        <Row key={item.key}>
                            {columnKey => {
                                switch (columnKey) {
                                    case 'label':
                                        return (
                                            <Cell>
                                                <ThumbnailCell thumbnail={item['image']} thumbnailAlt={item[columnKey]}>
                                                    {item['label']}
                                                </ThumbnailCell>
                                            </Cell>
                                        );
                                    case 'viewVariants':
                                        return (
                                            <Cell>
                                                {navigateVariants && item['type'] === 'ConfigurableProduct' && (
                                                    <ItemActionButton
                                                        actions={[
                                                            {
                                                                key: 'variants',
                                                                icon: <FolderOpenOutline />,
                                                                label: formatMessage(messages.viewVariants),
                                                                onPress: async item => {
                                                                    await onFilterChange({ folder: item, filters });
                                                                }
                                                            }
                                                        ]}
                                                        value={item}
                                                    />
                                                )}
                                            </Cell>
                                        );
                                    case 'price':
                                        return (
                                            <Cell>
                                                {item.price &&
                                                    formatNumber(item.price, {
                                                        style: 'currency',
                                                        currency: item.currency
                                                    })}
                                            </Cell>
                                        );
                                    default:
                                        return <Cell>{item[columnKey]}</Cell>;
                                }
                            }}
                        </Row>
                    )}
                </TableView>
                <Pagination />
            </Inventory>
        </div>
    );
};

ProductPicker.propTypes = {
    columns: PropTypes.array,
    multiple: PropTypes.bool,
    enablePreviewDateFilter: PropTypes.bool,
    selectionId: PropTypes.oneOf(['id', 'uid', 'sku', 'slug', 'combinedSku', 'combinedSlug']).isRequired
};

export default ProductPicker;
